import {
  resolvePendingInviteDecision,
  type PendingInvite,
  type PendingInviteLoad,
} from "./pending-invites.ts";

// Pure screen model for "Join account" (design draft 1, plate 06), shared by the native app and
// Guard Web so both surfaces say the same thing. No React, no supabase, no clock reads.
//
// Two product rules are encoded here and must not be quietly dropped:
//   1. Consent before the button. Plate 06: "Joining gives this company your shift records,
//      captures and location while you are on duty." A guard is handing over location data, so it
//      is stated in plain words above the primary action - never behind a terms link, and never
//      skipped by silently auto-accepting a lone invite.
//   2. Errors name the exit. A dead invite says what happened, that nothing was shared, and who to
//      ask next. No screen here says "invalid" and stops.

/** Plate 06 consent sentence. Rendered above the primary action on every join confirmation. */
export const JOIN_CONSENT_LINE =
  "Joining gives this company your shift records, captures and location while you are on duty.";

export type JoinAccountViewKind =
  | "loading"
  | "confirm"
  | "choose"
  | "none"
  | "unavailable"
  | "error";

export interface JoinAccountView {
  readonly kind: JoinAccountViewKind;
  readonly headline: string;
  readonly body: string;
  /** Consent sentence, present only when a join action is offered. */
  readonly consentLine: string | null;
  readonly invites: readonly PendingInvite[];
  readonly selectedInvite: PendingInvite | null;
  /** Primary button label; names the account so nobody joins the wrong company by muscle memory. */
  readonly primaryLabel: string | null;
  readonly primaryEnabled: boolean;
  /** "Signed in as ..." footer, so a wrong-address invite is visible before the tap. */
  readonly signedInLine: string | null;
  /** Result of the last accept attempt, already mapped to safe copy. */
  readonly errorLine: string | null;
  readonly whatToDoLine: string | null;
}

export interface JoinAccountInput {
  /** null while the first lookup is in flight. */
  readonly load: PendingInviteLoad | null;
  readonly selectedInviteId: string | null;
  /** Safe sentence from acceptPendingInvite, or null. */
  readonly acceptFailure: string | null;
  readonly signedInEmail: string | null;
  readonly busy: boolean;
}

export function presentJoinAccount(input: JoinAccountInput): JoinAccountView {
  const signedInLine = formatSignedInLine(input.signedInEmail);
  const failure = input.acceptFailure === null ? null : describeAcceptFailure(input.acceptFailure, input.signedInEmail);

  if (input.load === null) {
    return {
      kind: "loading",
      headline: "Checking invitations",
      body: "Looking for workspaces that invited this email.",
      consentLine: null,
      invites: [],
      selectedInvite: null,
      primaryLabel: null,
      primaryEnabled: false,
      signedInLine,
      errorLine: null,
      whatToDoLine: null,
    };
  }

  if (input.load.status === "unavailable") {
    return {
      kind: "unavailable",
      headline: "Can't check invitations yet",
      body: input.load.reason,
      consentLine: null,
      invites: [],
      selectedInvite: null,
      primaryLabel: null,
      primaryEnabled: false,
      signedInLine,
      errorLine: null,
      whatToDoLine: askForInviteLine(input.signedInEmail),
    };
  }

  if (input.load.status === "failed") {
    return {
      kind: "error",
      headline: "Couldn't check invitations",
      body: input.load.reason,
      consentLine: null,
      invites: [],
      selectedInvite: null,
      primaryLabel: null,
      primaryEnabled: false,
      signedInLine,
      errorLine: null,
      whatToDoLine: null,
    };
  }

  const decision = resolvePendingInviteDecision(input.load.invites);

  if (decision.kind === "none") {
    return {
      kind: "none",
      headline: failure?.headline ?? "No workspace access",
      body: failure?.body ?? "No company has invited this email yet, and this user is not a member of one.",
      consentLine: null,
      invites: [],
      selectedInvite: null,
      primaryLabel: null,
      primaryEnabled: false,
      signedInLine,
      errorLine: null,
      whatToDoLine: failure?.whatToDo ?? askForInviteLine(input.signedInEmail),
    };
  }

  if (decision.kind === "single") {
    const invite = decision.invite;
    return {
      kind: "confirm",
      headline: invite.accountName,
      body: "You have been invited",
      consentLine: JOIN_CONSENT_LINE,
      invites: [invite],
      selectedInvite: invite,
      primaryLabel: `Join ${invite.accountName}`,
      primaryEnabled: !input.busy,
      signedInLine,
      errorLine: failure?.headline ?? null,
      whatToDoLine: failure?.whatToDo ?? null,
    };
  }

  const selected = pickSelected(decision.invites, input.selectedInviteId, decision.suggestedInviteId);
  return {
    kind: "choose",
    headline: "Which account first?",
    body: "You can join the other one later from Account. Only one workspace is active at a time.",
    consentLine: JOIN_CONSENT_LINE,
    invites: decision.invites,
    selectedInvite: selected,
    primaryLabel: selected === null ? null : `Join ${selected.accountName}`,
    primaryEnabled: selected !== null && !input.busy,
    signedInLine,
    errorLine: failure?.headline ?? null,
    whatToDoLine: failure?.whatToDo ?? null,
  };
}

export interface AcceptFailureCopy {
  readonly headline: string;
  readonly body: string;
  readonly whatToDo: string | null;
}

/**
 * Turn an allowlisted server sentence into plate-06 copy: what happened, what it means for the
 * guard's data, and who to ask. `reason` has already passed through mapAcceptFailure, so an
 * unrecognised value here is the generic sentence and is shown as-is.
 */
export function describeAcceptFailure(reason: string, signedInEmail: string | null): AcceptFailureCopy {
  switch (reason) {
    case "Pending invite was not found for this email.":
      return {
        headline: "This invitation is no longer open",
        body: "It was withdrawn or already used. Nothing was shared with that company.",
        whatToDo: askForInviteLine(signedInEmail),
      };
    case "Pending invite is expired.":
      return {
        headline: "This invitation expired",
        body: "Invitations time out so an old email cannot be used later. Nothing was shared.",
        whatToDo: askForInviteLine(signedInEmail),
      };
    case "Active account membership already exists for this email.":
      return {
        headline: "You are already a member",
        body: "This email already has active access to that workspace.",
        whatToDo: "Retry to load your workspace.",
      };
    case "Account membership is suspended.":
      return {
        headline: "This membership is suspended",
        body: "Access was paused by that company, so the invitation cannot be accepted.",
        whatToDo: "Ask your SecOps admin or supervisor to restore access.",
      };
    default:
      return {
        headline: reason,
        body: "Nothing was changed. You can try again.",
        whatToDo: null,
      };
  }
}

/** "Ask ... to send a new invitation to <address>" - the second real exit from a dead invite. */
export function askForInviteLine(signedInEmail: string | null): string {
  const email = normalizeEmail(signedInEmail);
  return email === null
    ? "Ask your SecOps admin or supervisor to send an invitation to this email address."
    : `Ask your SecOps admin or supervisor to send an invitation to ${email}.`;
}

export function formatSignedInLine(signedInEmail: string | null): string | null {
  const email = normalizeEmail(signedInEmail);
  return email === null ? null : `Signed in as ${email}`;
}

/** "Officer · sent Aug 5"-style secondary line for a picker row. Omits parts it does not have. */
export function formatInviteMeta(invite: PendingInvite): string {
  const parts = [formatInviteRole(invite.role)];
  const sent = formatInviteDate(invite.createdAt);
  if (sent !== null) parts.push(`sent ${sent}`);
  return parts.join(" · ");
}

export function formatInviteRole(role: string): string {
  switch (role) {
    case "guard":
      return "Officer";
    case "supervisor":
      return "Supervisor";
    case "admin":
      return "Admin";
    default:
      return role.length > 0 ? role.charAt(0).toUpperCase() + role.slice(1) : "Officer";
  }
}

/** Short "Aug 5" form for an ISO timestamp; null when the value is missing or unparseable. */
export function formatInviteDate(value: string | null): string | null {
  if (value === null) return null;
  const parsed = new Date(value);
  const time = parsed.getTime();
  if (!Number.isFinite(time)) return null;
  return `${MONTHS[parsed.getUTCMonth()]} ${parsed.getUTCDate()}`;
}

/** Two-letter workspace monogram for the plate-06 avatar. */
export function accountMonogram(accountName: string): string {
  const words = accountName.trim().split(/\s+/).filter((word) => word.length > 0);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return `${words[0]!.charAt(0)}${words[1]!.charAt(0)}`.toUpperCase();
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

function pickSelected(
  invites: readonly PendingInvite[],
  selectedInviteId: string | null,
  suggestedInviteId: string,
): PendingInvite | null {
  const chosen = selectedInviteId === null ? null : invites.find((invite) => invite.inviteId === selectedInviteId);
  return chosen ?? invites.find((invite) => invite.inviteId === suggestedInviteId) ?? invites[0] ?? null;
}

function normalizeEmail(value: string | null): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
}
